import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { useMe } from '../App.js';
import { useI18n } from '../i18n.js';

/** OAuth consent for MCP clients (Claude Code and others) connecting to /mcp. */
export function Consent() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const me = useMe();
  const clientId = params.get('client_id') ?? '';
  const scopes = (params.get('scope') ?? '').split(' ').filter(Boolean);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const client = useQuery({
    queryKey: ['oauth-client', clientId],
    enabled: Boolean(clientId) && Boolean(me.data),
    queryFn: async () => {
      const res = await fetch(`/api/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`, {
        credentials: 'include',
      });
      if (!res.ok) return null;
      return (await res.json()) as { client_name?: string; client_uri?: string; logo_uri?: string };
    },
  });

  if (me.isPending) return <div className="center muted">{t('loading')}</div>;
  if (me.error) return <Navigate to={`/login${window.location.search}`} replace />;

  async function answer(accept: boolean) {
    setBusy(true);
    setError(null);
    const res = await fetch('/api/auth/oauth2/consent', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ accept, oauth_query: window.location.search.slice(1) }),
    });
    const body = await res.json().catch(() => ({}));
    const url = body.url ?? body.redirect_uri ?? body.redirectURI;
    if (res.ok && url) {
      window.location.assign(url);
      return;
    }
    setBusy(false);
    setError(body.message ?? body.error_description ?? body.error ?? `HTTP ${res.status}`);
  }

  const name = client.data?.client_name ?? clientId;
  return (
    <div className="stack">
      <h2 className="bare-title">{t('consentTitle')}</h2>
      <p>
        <strong>{name}</strong> {t('consentText')}
      </p>
      <dl className="facts">
        <dt>{t('email')}</dt>
        <dd>{me.data.user.email}</dd>
        {scopes.length > 0 && (
          <>
            <dt>{t('scopes')}</dt>
            <dd>{scopes.join(', ')}</dd>
          </>
        )}
      </dl>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button className="primary" disabled={busy} onClick={() => answer(true)}>
          {t('allow')}
        </button>
        <button disabled={busy} onClick={() => answer(false)}>
          {t('deny')}
        </button>
      </div>
    </div>
  );
}
